import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade1Zhuyin.css";

/** Helper to shuffle arrays */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade1Zhuyin
 *  - Teaches only Zhuyin (Bopomofo)
 *  - Contains a single quiz pool of 25 questions
 *  - Quick Quiz picks random from the 25
 *  - Final Quiz uses all 25 (or we can reuse the same set)
 */

/** The main Zhuyin table snippet */
const zhuyinTable = (
  <div key="zhuyin-table" className="zhuyin-table-wrapper">
    <h4>Full Zhuyin (Bopomofo) Table</h4>
    <p>
      There are 37 basic symbols plus tone marks. Below are the main symbols (arranged
      in a common learning order), along with approximate Pinyin equivalents. 
      Mandarin has 4 main tones + neutral: <strong>ˉ</strong> (1st), <strong>ˊ</strong> (2nd), <strong>ˇ</strong> (3rd), <strong>ˋ</strong> (4th), and <strong>˙</strong> (neutral).
    </p>
    <table className="zhuyin-table">
      <thead>
        <tr>
          <th>Symbol</th>
          <th>Pinyin Approx.</th>
          <th>Notes / Example</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>ㄅ</td><td>b</td><td>Unaspirated “b” (e.g. 八 bā)</td></tr>
        <tr><td>ㄆ</td><td>p</td><td>Aspirated “p” (e.g. 怕 pà)</td></tr>
        <tr><td>ㄇ</td><td>m</td><td>“m” (妈 mā)</td></tr>
        <tr><td>ㄈ</td><td>f</td><td>“f” (发 fā)</td></tr>
        <tr><td>ㄉ</td><td>d</td><td>Unaspirated “d” (大 dà)</td></tr>
        <tr><td>ㄊ</td><td>t</td><td>Aspirated “t” (他 tā)</td></tr>
        <tr><td>ㄋ</td><td>n</td><td>“n” (你 nǐ)</td></tr>
        <tr><td>ㄌ</td><td>l</td><td>“l” (了 le)</td></tr>
        <tr><td>ㄍ</td><td>g</td><td>Unaspirated “g” (哥 gē)</td></tr>
        <tr><td>ㄎ</td><td>k</td><td>Aspirated “k” (苛 kē)</td></tr>
        <tr><td>ㄏ</td><td>h</td><td>“h” (喝 hē)</td></tr>
        <tr><td>ㄐ</td><td>j</td><td>(e.g. 鸡 jī)</td></tr>
        <tr><td>ㄑ</td><td>q</td><td>(轻 qīng)</td></tr>
        <tr><td>ㄒ</td><td>x</td><td>(西 xī)</td></tr>
        <tr><td>ㄓ</td><td>zh</td><td>(知 zhī)</td></tr>
        <tr><td>ㄔ</td><td>ch</td><td>(吃 chī)</td></tr>
        <tr><td>ㄕ</td><td>sh</td><td>(师 shī)</td></tr>
        <tr><td>ㄖ</td><td>r</td><td>(日 rì)</td></tr>
        <tr><td>ㄗ</td><td>z</td><td>Unaspirated “ts” (子 zǐ)</td></tr>
        <tr><td>ㄘ</td><td>c</td><td>Aspirated “ts” (次 cì)</td></tr>
        <tr><td>ㄙ</td><td>s</td><td>(四 sì)</td></tr>
        <tr><td>ㄧ</td><td>i</td><td>(一 yī)</td></tr>
        <tr><td>ㄨ</td><td>u</td><td>(乌 wū)</td></tr>
        <tr><td>ㄩ</td><td>ü</td><td>(迂 yū)</td></tr>
        <tr><td>ㄚ</td><td>a</td><td>(啊 a)</td></tr>
        <tr><td>ㄛ</td><td>o</td><td>(喔 ō)</td></tr>
        <tr><td>ㄜ</td><td>e</td><td>(鹅 é)</td></tr>
        <tr><td>ㄝ</td><td>ê</td><td>“eh” (often merged)</td></tr>
        <tr><td>ㄞ</td><td>ai</td><td>like “eye” (爱 ài)</td></tr>
        <tr><td>ㄟ</td><td>ei</td><td>like “ay” (给 gěi)</td></tr>
        <tr><td>ㄠ</td><td>ao</td><td>(好 hǎo)</td></tr>
        <tr><td>ㄡ</td><td>ou</td><td>(口 kǒu)</td></tr>
        <tr><td>ㄢ</td><td>an</td><td>(安 ān)</td></tr>
        <tr><td>ㄣ</td><td>en</td><td>(恩 ēn)</td></tr>
        <tr><td>ㄤ</td><td>ang</td><td>(方 fāng)</td></tr>
        <tr><td>ㄥ</td><td>eng</td><td>(朋 péng)</td></tr>
        <tr><td>ㄦ</td><td>er</td><td>retroflex (儿 ér)</td></tr>
      </tbody>
    </table>

    <p style={{ marginTop: "0.5em" }}>
      <strong>Tone Marks:</strong> 
      <br />1st tone: ˉ (e.g. ㄧˉ) 
      <br />2nd tone: ˊ 
      <br />3rd tone: ˇ 
      <br />4th tone: ˋ 
      <br />Neutral: ˙ or sometimes no mark
    </p>
  </div>
);

/** We have 25 questions about Zhuyin. */
const zhuyinQuestions = [
  {
    question: "What is Zhuyin?",
    correctAnswer: "A phonetic system using symbols like ㄅ, ㄆ, ㄇ",
    options: [
      "An English-based writing system",
      "A phonetic system using symbols like ㄅ, ㄆ, ㄇ",
      "A style of cursive Chinese characters",
    ],
  },
  {
    question: "Zhuyin is also called?",
    correctAnswer: "Bopomofo",
    options: [
      "Bingbong",
      "Bopomofo",
      "Zhongwen",
    ],
  },
  {
    question: "Which region commonly uses Zhuyin?",
    correctAnswer: "Taiwan",
    options: [
      "Only Europe",
      "Taiwan",
      "Malaysia exclusively",
    ],
  },
  {
    question: "Which is one of the first Zhuyin symbols?",
    correctAnswer: "ㄅ",
    options: [
      "ㄪ",
      "ㄹ",
      "ㄅ",
    ],
  },
  {
    question: "Zhuyin has how many basic symbols (excluding tone marks)?",
    correctAnswer: "37",
    options: [
      "Only 10",
      "37",
      "Over 100",
    ],
  },
  {
    question: "How might '你好' be written in Zhuyin?",
    correctAnswer: "ㄋㄧˇ ㄏㄠˇ",
    options: [
      "ni hao",
      "ㄋㄧˇ ㄏㄠˇ",
      "ㄣㄧˊ ㄏㄠˉ",
    ],
  },
  {
    question: "Zhuyin represents?",
    correctAnswer: "Mandarin sounds with unique symbols",
    options: [
      "Cantonese sounds only",
      "Mandarin sounds with unique symbols",
      "No sounds, just pictures",
    ],
  },
  {
    question: "Zhuyin is typically taught to children in?",
    correctAnswer: "Taiwanese schools before full Chinese characters",
    options: [
      "European schools only",
      "Taiwanese schools before full Chinese characters",
      "No schools at all",
    ],
  },
  {
    question: "A common name for ㄅㄆㄇㄈ is?",
    correctAnswer: "Bopomofo, from the first symbols",
    options: [
      "Zhiyin",
      "Bopomofo, from the first symbols",
      "Xinwen system",
    ],
  },
  {
    question: "Zhuyin includes tone marks placed?",
    correctAnswer: "Around the Zhuyin symbols",
    options: [
      "Nowhere, no tones exist",
      "Above letters like 'a, b, c'",
      "Around the Zhuyin symbols",
    ],
  },
  {
    question: "Why do some prefer Zhuyin?",
    correctAnswer: "It doesn’t mix with the Latin alphabet, focusing on Chinese sounds",
    options: [
      "It’s easier to write English with it",
      "It doesn’t mix with the Latin alphabet, focusing on Chinese sounds",
      "It’s only for ancient texts",
    ],
  },
  {
    question: "Which is a valid Zhuyin symbol?",
    correctAnswer: "ㄇ",
    options: [
      "ㄾ",
      "ㄇ",
      " (apple)",
    ],
  },
  {
    question: "Zhuyin can appear?",
    correctAnswer: "Beside or above Chinese characters as a reading aid",
    options: [
      "Only typed alone with no reference to characters",
      "On top of English text",
      "Beside or above Chinese characters as a reading aid",
    ],
  },
  {
    question: "Similar to Pinyin, Zhuyin also has?",
    correctAnswer: "4 main tones plus a neutral tone",
    options: [
      "No concept of tones at all",
      "20 different tones",
      "4 main tones plus a neutral tone",
    ],
  },
  {
    question: "Bopomofo was introduced around?",
    correctAnswer: "Early 20th century, to standardize Mandarin teaching",
    options: [
      "Only 5 years ago",
      "Early 20th century, to standardize Mandarin teaching",
      "2,000 years ago in ancient China",
    ],
  },
  {
    question: "Which might be a reason children use Zhuyin in Taiwan?",
    correctAnswer: "It helps them learn to read characters gradually",
    options: [
      "They hate Pinyin",
      "It helps them learn to read characters gradually",
      "It’s required in Mainland China only",
    ],
  },
  {
    question: "Zhuyin can also be used for?",
    correctAnswer: "Typing Chinese on devices (Zhuyin input)",
    options: [
      "Typing English only",
      "No use in technology",
      "Typing Chinese on devices (Zhuyin input)",
    ],
  },
  {
    question: "Which system uses Latin letters: Pinyin or Zhuyin?",
    correctAnswer: "Pinyin uses Latin letters, Zhuyin does not",
    options: [
      "Both do",
      "Pinyin uses Latin letters, Zhuyin does not",
      "Zhuyin uses them more than Pinyin",
    ],
  },
  {
    question: "Which symbol set is “ㄅㄆㄇㄈ” from?",
    correctAnswer: "Zhuyin",
    options: [
      "Pinyin",
      "Zhuyin",
      "Korean Hangul",
    ],
  },
  {
    question: "If someone uses ㄍㄨㄛˊ ㄩˇ, they’re writing 'guó yǔ' in?",
    correctAnswer: "Zhuyin for Mandarin 'Chinese language'",
    options: [
      "Pinyin for Japanese words",
      "Zhuyin for Mandarin 'Chinese language'",
      "English abbreviation",
    ],
  },
  {
    question: "The shape of Zhuyin symbols is derived from?",
    correctAnswer: "Parts of Chinese characters",
    options: [
      "Random scribbles",
      "Greek letters",
      "Parts of Chinese characters",
    ],
  },
  {
    question: "Which system do children in Mainland China mostly learn?",
    correctAnswer: "Pinyin",
    options: [
      "Zhuyin only",
      "No phonetic system",
      "Pinyin",
    ],
  },
  {
    question: "Which statement is true about Zhuyin vs. Pinyin?",
    correctAnswer: "Both represent Mandarin sounds, just with different symbols",
    options: [
      "They represent different languages entirely",
      "Both represent Mandarin sounds, just with different symbols",
      "Zhuyin is only for ancient scripts, Pinyin for modern",
    ],
  },
  {
    question: "Which system is sometimes placed beside characters vertically in reading books?",
    correctAnswer: "Zhuyin",
    options: [
      "Pinyin horizontally only",
      "Zhuyin",
      "Neither system does that",
    ],
  },
];

export default function Grade1Zhuyin() {
  // We only have ONE topic (Zhuyin), so we can skip multi-topic logic
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // Timer for final quiz
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Generate a random question from the zhuyinQuestions
  const generateQuiz = () => {
    const randomQ = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: randomQ.question,
      correctAnswer: randomQ.correctAnswer,
      options: shuffleArray([...randomQ.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // Handle Quick Quiz answer
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // auto-generate next after short delay
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Start Final Quiz (use the entire zhuyinQuestions array)
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  // Final quiz answer
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  // UI
  return (
    <div className="g1chi-page">
      <h1 className="g1chi-title">Grade 1 Zhuyin (Bopomofo) Lessons</h1>
      <p className="g1chi-description">
        Learn the basic symbols and tones of Zhuyin!
      </p>

      {/* Lesson Content (Zhuyin only) */}
      <div className="g1chi-lesson-content">
        <h2 className="g1chi-lesson-title">注音 (Zhuyin)</h2>
        <p className="g1chi-lesson-description">
          Zhuyin (Bopomofo) is a phonetic system for Chinese, often used in Taiwan.
          It has 37 symbols plus tone marks for Mandarin’s 4 tones + neutral.
        </p>
        <p className="g1chi-lesson-example">
          <strong>Example:</strong> “你好” might be ㄋㄧˇ ㄏㄠˇ!
        </p>
        <ul className="g1chi-lesson-steps">
          <li>Zhuyin uses symbols like ㄅ, ㄆ, ㄇ for consonants.</li>
          <li>Vowel symbols like ㄧ (i), ㄨ (u), ㄩ (ü) are combined for syllables.</li>
          <li>4 main tones + a neutral tone are added around the symbols.</li>
          <li>Many children in Taiwan learn Zhuyin before learning characters.</li>
          <li>Check out the chart below to see all of them!</li>
          {zhuyinTable}
        </ul>
      </div>

      {/* QUIZ SECTION */}
      <div className="g1chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g1chi-quick-quiz-container">
          <h2 className="g1chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g1chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g1chi-quiz-question">{quiz.question}</p>
              <div className="g1chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g1chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g1chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g1chi-final-quiz-container">
          <h2 className="g1chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g1chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g1chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>
                Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
              </p>
              <p className="g1chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g1chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleFinalQuizAnswer(choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && !timeLeft && (
                <p>
                  Final Score: {finalQuizScore}/{finalQuizQuestions.length}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Back link */}
      <Link to="/ChineseSubject" className="g1chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
